import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QrCode, Link, Share2, CheckCircle, AlertTriangle, UserPlus, Users, Eye, Radio, Trash2, Play, X, Zap, Clock, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';

export default function LinkingPanel({ user }) {
    const [myCode, setMyCode] = useState(user.linkCode || null);
    const [inputCode, setInputCode] = useState('');
    const [linkStatus, setLinkStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingDevices, setLoadingDevices] = useState(false);

    // Estado para dispositivos vinculados
    const [linkedDevices, setLinkedDevices] = useState({ watching: [], observers: [] });
    const [showLinkedDevices, setShowLinkedDevices] = useState(true);

    // Estado para simulación
    const [simulationActive, setSimulationActive] = useState(false);
    const [simulationStatus, setSimulationStatus] = useState(null);

    // Cargar dispositivos vinculados al montar
    useEffect(() => {
        fetchLinkedDevices();
    }, [user.uid]);

    const fetchLinkedDevices = async () => {
        setLoadingDevices(true);
        try {
            const res = await api.auth.getLinkedDevices(user.uid);
            setLinkedDevices(res.data || { watching: [], observers: [] });
        } catch (err) {
            console.error("Error fetching linked devices:", err);
        } finally {
            setLoadingDevices(false);
        }
    };

    const handleGenerateCode = async () => {
        setLoading(true);
        try {
            const res = await api.auth.generateCode(user.uid);
            setMyCode(res.data.code);
        } catch (err) {
            console.error("Error generating code:", err);
            setLinkStatus({ type: 'error', message: 'Error generando código.' });
        } finally {
            setLoading(false);
        }
    };

    const handleLinkUser = async (e) => {
        e.preventDefault();
        if (!inputCode) return;

        setLoading(true);
        setLinkStatus(null);
        try {
            const res = await api.auth.linkUser(user.uid, inputCode);
            if (res.data.success) {
                setLinkStatus({
                    type: 'success',
                    message: `¡Vinculado con éxito! Ahora recibirás alertas de ${res.data.targetUser.displayName || 'Usuario'}.`
                });
                setInputCode('');
                // Recargar dispositivos
                fetchLinkedDevices();
            } else {
                setLinkStatus({ type: 'error', message: res.data.message });
            }
        } catch (err) {
            console.error("Link error:", err);
            setLinkStatus({ type: 'error', message: 'Error al intentar vincular.' });
        } finally {
            setLoading(false);
        }
    };

    const handleUnlink = async (targetUid, type) => {
        if (!confirm(`¿Estás seguro de que deseas desvincular este dispositivo?`)) return;

        try {
            await api.auth.unlinkDevice(user.uid, targetUid, type);
            fetchLinkedDevices();
        } catch (err) {
            console.error("Error unlinking device:", err);
            alert('Error al desvincular el dispositivo.');
        }
    };

    const handleStartSimulation = async () => {
        if (linkedDevices.watching.length === 0 && linkedDevices.observers.length === 0) {
            alert('⚠️ No tienes dispositivos vinculados para iniciar la simulación.');
            return;
        }

        setSimulationActive(true);
        setSimulationStatus({ type: 'loading', message: 'Activando dispositivos vinculados...' });

        try {
            // Simular activación de dispositivos
            await new Promise(resolve => setTimeout(resolve, 1500));

            // Enviar alerta de simulación a través de la API
            await api.alerts.create({
                type: 'simulation',
                level: 'info',
                message: '🧪 Simulación de alerta iniciada desde dispositivo vinculado',
                timestamp: new Date().toISOString(),
                initiatedBy: user.uid,
                linkedDevices: [
                    ...linkedDevices.watching.map(d => d.uid),
                    ...linkedDevices.observers.map(d => d.uid)
                ]
            });

            setSimulationStatus({
                type: 'success',
                message: `✅ Simulación activa. ${linkedDevices.watching.length + linkedDevices.observers.length} dispositivos notificados.`
            });

            // Mantener la simulación activa por 10 segundos
            setTimeout(() => {
                setSimulationActive(false);
                setSimulationStatus(null);
            }, 10000);

        } catch (err) {
            console.error("Simulation error:", err);
            setSimulationStatus({ type: 'error', message: 'Error al iniciar simulación.' });
            setTimeout(() => setSimulationActive(false), 3000);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Fecha desconocida';
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const totalLinkedDevices = linkedDevices.watching.length + linkedDevices.observers.length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* Header con contador de dispositivos */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                    borderRadius: '1.5rem',
                    padding: '1.5rem',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '1rem'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4)'
                    }}>
                        <Users size={24} color="white" />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#fff' }}>Red de Dispositivos</h2>
                        <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.9rem' }}>
                            {totalLinkedDevices} dispositivo{totalLinkedDevices !== 1 ? 's' : ''} vinculado{totalLinkedDevices !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>

                <button
                    onClick={fetchLinkedDevices}
                    style={{
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '0.75rem',
                        padding: '0.75rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: '#94a3b8',
                        transition: 'all 0.2s'
                    }}
                    disabled={loadingDevices}
                >
                    <RefreshCw size={18} className={loadingDevices ? 'spinning' : ''} />
                    <span style={{ fontSize: '0.85rem' }}>Actualizar</span>
                </button>
            </motion.div>

            {/* Grid principal */}
            <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>

                {/* Card 1: My Link Code */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-card" style={{ padding: '1.5rem' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                        <div className="icon-box" style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa' }}>
                            <Share2 size={24} />
                        </div>
                        <div>
                            <h3 style={{ margin: 0 }}>Compartir Acceso</h3>
                            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.9rem' }}>Permite a otros recibir tus alertas</p>
                        </div>
                    </div>

                    <div style={{ background: 'rgba(15, 23, 42, 0.6)', borderRadius: '1rem', padding: '1.5rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
                        {myCode ? (
                            <>
                                <p style={{ color: '#94a3b8', marginBottom: '0.5rem' }}>Tu código de vinculación:</p>
                                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', letterSpacing: '4px', color: '#fff', textShadow: '0 0 20px rgba(59, 130, 246, 0.5)' }}>
                                    {myCode}
                                </div>
                                <button
                                    onClick={() => { navigator.clipboard.writeText(myCode); alert('Código copiado!'); }}
                                    style={{ marginTop: '1rem', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: 'pointer' }}
                                >
                                    Copiar Código
                                </button>
                            </>
                        ) : (
                            <div style={{ padding: '1rem 0' }}>
                                <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>No tienes un código activo.</p>
                                <button onClick={handleGenerateCode} className="btn btn-primary" disabled={loading}>
                                    {loading ? 'Generando...' : 'Generar Código'}
                                </button>
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* Card 2: Link to Others */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="glass-card" style={{ padding: '1.5rem' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                        <div className="icon-box" style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#34d399' }}>
                            <UserPlus size={24} />
                        </div>
                        <div>
                            <h3 style={{ margin: 0 }}>Monitorear Cuenta</h3>
                            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.9rem' }}>Recibe alertas de otro dispositivo</p>
                        </div>
                    </div>

                    <form onSubmit={handleLinkUser}>
                        <div className="input-group" style={{ marginBottom: '1.5rem' }}>
                            <QrCode size={20} style={{ position: 'absolute', left: 15, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                            <input
                                type="text"
                                placeholder="Ingresa el código (ej. A7X92B)"
                                className="input-text"
                                style={{ paddingLeft: 45, width: '100%', textTransform: 'uppercase' }}
                                value={inputCode}
                                onChange={e => setInputCode(e.target.value.toUpperCase())}
                            />
                        </div>

                        <button type="submit" className="btn btn-primary full-width" disabled={loading || !inputCode}>
                            {loading ? 'Vinculando...' : 'Vincular Dispositivo'}
                        </button>

                        {linkStatus && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                style={{
                                    marginTop: '1rem',
                                    padding: '1rem',
                                    borderRadius: '0.5rem',
                                    background: linkStatus.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                    color: linkStatus.type === 'success' ? '#34d399' : '#f87171',
                                    border: `1px solid ${linkStatus.type === 'success' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                {linkStatus.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                                <p style={{ margin: 0, fontSize: '0.9rem' }}>{linkStatus.message}</p>
                            </motion.div>
                        )}
                    </form>
                </motion.div>
            </div>

            {/* Sección de dispositivos vinculados */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass-card"
                style={{ padding: '1.5rem' }}
            >
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '1.5rem',
                    flexWrap: 'wrap',
                    gap: '1rem'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div className="icon-box" style={{ background: 'rgba(139, 92, 246, 0.2)', color: '#a78bfa' }}>
                            <Link size={24} />
                        </div>
                        <div>
                            <h3 style={{ margin: 0 }}>Dispositivos Vinculados</h3>
                            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.9rem' }}>
                                Ve quién puede recibir y enviarte alertas
                            </p>
                        </div>
                    </div>

                    {/* Botón Iniciar Simulación */}
                    <button
                        onClick={handleStartSimulation}
                        disabled={simulationActive || totalLinkedDevices === 0}
                        style={{
                            background: simulationActive
                                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            border: 'none',
                            borderRadius: '1rem',
                            padding: '0.75rem 1.5rem',
                            color: 'white',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            cursor: totalLinkedDevices === 0 ? 'not-allowed' : 'pointer',
                            opacity: totalLinkedDevices === 0 ? 0.5 : 1,
                            boxShadow: simulationActive
                                ? '0 4px 20px rgba(16, 185, 129, 0.4)'
                                : '0 4px 20px rgba(245, 158, 11, 0.4)',
                            transition: 'all 0.3s'
                        }}
                    >
                        {simulationActive ? (
                            <>
                                <Zap size={18} />
                                Simulación Activa
                            </>
                        ) : (
                            <>
                                <Play size={18} />
                                Iniciar Simulación
                            </>
                        )}
                    </button>
                </div>

                {/* Estado de simulación */}
                <AnimatePresence>
                    {simulationStatus && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            style={{
                                marginBottom: '1.5rem',
                                padding: '1rem',
                                borderRadius: '0.75rem',
                                background: simulationStatus.type === 'success'
                                    ? 'rgba(16, 185, 129, 0.15)'
                                    : simulationStatus.type === 'error'
                                        ? 'rgba(239, 68, 68, 0.15)'
                                        : 'rgba(59, 130, 246, 0.15)',
                                border: `1px solid ${simulationStatus.type === 'success'
                                        ? 'rgba(16, 185, 129, 0.3)'
                                        : simulationStatus.type === 'error'
                                            ? 'rgba(239, 68, 68, 0.3)'
                                            : 'rgba(59, 130, 246, 0.3)'
                                    }`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem'
                            }}
                        >
                            {simulationStatus.type === 'loading' && (
                                <div className="spinner" style={{ width: 20, height: 20 }} />
                            )}
                            {simulationStatus.type === 'success' && <CheckCircle size={20} color="#34d399" />}
                            {simulationStatus.type === 'error' && <AlertTriangle size={20} color="#f87171" />}
                            <span style={{
                                color: simulationStatus.type === 'success'
                                    ? '#34d399'
                                    : simulationStatus.type === 'error'
                                        ? '#f87171'
                                        : '#60a5fa',
                                fontSize: '0.95rem'
                            }}>
                                {simulationStatus.message}
                            </span>
                        </motion.div>
                    )}
                </AnimatePresence>

                {loadingDevices ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                        <div className="spinner" style={{ margin: '0 auto 1rem' }} />
                        Cargando dispositivos...
                    </div>
                ) : totalLinkedDevices === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '3rem 2rem',
                        background: 'rgba(15, 23, 42, 0.5)',
                        borderRadius: '1rem',
                        border: '1px dashed rgba(148, 163, 184, 0.2)'
                    }}>
                        <Users size={48} style={{ color: '#475569', marginBottom: '1rem' }} />
                        <h4 style={{ margin: '0 0 0.5rem', color: '#94a3b8' }}>Sin dispositivos vinculados</h4>
                        <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
                            Comparte tu código o usa el código de alguien más para vincular dispositivos
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                        {/* Dispositivos que estoy observando */}
                        {linkedDevices.watching.length > 0 && (
                            <div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '0.75rem',
                                    paddingBottom: '0.5rem',
                                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                                }}>
                                    <Eye size={16} color="#60a5fa" />
                                    <span style={{ color: '#60a5fa', fontSize: '0.85rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Observando ({linkedDevices.watching.length})
                                    </span>
                                    <span style={{ color: '#64748b', fontSize: '0.8rem' }}>— Recibes sus alertas</span>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {linkedDevices.watching.map((device, index) => (
                                        <motion.div
                                            key={device.uid}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '1rem',
                                                background: 'rgba(59, 130, 246, 0.08)',
                                                borderRadius: '0.75rem',
                                                border: '1px solid rgba(59, 130, 246, 0.15)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                <div style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '50%',
                                                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontWeight: 'bold',
                                                    fontSize: '1rem'
                                                }}>
                                                    {device.displayName?.charAt(0)?.toUpperCase() || 'U'}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: '600', color: '#fff' }}>{device.displayName}</div>
                                                    <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{device.email}</div>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        marginTop: '0.25rem'
                                                    }}>
                                                        <Clock size={12} color="#64748b" />
                                                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                            Vinculado: {formatDate(device.linkedAt)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '2rem',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '600',
                                                    background: device.status === 'activo' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                                    color: device.status === 'activo' ? '#34d399' : '#f87171'
                                                }}>
                                                    {device.status === 'activo' ? '● Activo' : '○ Inactivo'}
                                                </span>
                                                <button
                                                    onClick={() => handleUnlink(device.uid, 'watching')}
                                                    style={{
                                                        background: 'rgba(239, 68, 68, 0.1)',
                                                        border: 'none',
                                                        borderRadius: '0.5rem',
                                                        padding: '0.5rem',
                                                        cursor: 'pointer',
                                                        color: '#f87171',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                    title="Desvincular"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Dispositivos que me observan */}
                        {linkedDevices.observers.length > 0 && (
                            <div style={{ marginTop: linkedDevices.watching.length > 0 ? '1rem' : 0 }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '0.75rem',
                                    paddingBottom: '0.5rem',
                                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                                }}>
                                    <Radio size={16} color="#a78bfa" />
                                    <span style={{ color: '#a78bfa', fontSize: '0.85rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Observadores ({linkedDevices.observers.length})
                                    </span>
                                    <span style={{ color: '#64748b', fontSize: '0.8rem' }}>— Reciben tus alertas</span>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {linkedDevices.observers.map((device, index) => (
                                        <motion.div
                                            key={device.uid}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '1rem',
                                                background: 'rgba(139, 92, 246, 0.08)',
                                                borderRadius: '0.75rem',
                                                border: '1px solid rgba(139, 92, 246, 0.15)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                <div style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '50%',
                                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontWeight: 'bold',
                                                    fontSize: '1rem'
                                                }}>
                                                    {device.displayName?.charAt(0)?.toUpperCase() || 'U'}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: '600', color: '#fff' }}>{device.displayName}</div>
                                                    <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{device.email}</div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '2rem',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '600',
                                                    background: device.status === 'activo' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                                    color: device.status === 'activo' ? '#34d399' : '#f87171'
                                                }}>
                                                    {device.status === 'activo' ? '● Activo' : '○ Inactivo'}
                                                </span>
                                                <button
                                                    onClick={() => handleUnlink(device.uid, 'observer')}
                                                    style={{
                                                        background: 'rgba(239, 68, 68, 0.1)',
                                                        border: 'none',
                                                        borderRadius: '0.5rem',
                                                        padding: '0.5rem',
                                                        cursor: 'pointer',
                                                        color: '#f87171',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                    title="Desvincular"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </motion.div>

            {/* Estilo para spinner */}
            <style>{`
                .spinner {
                    width: 24px;
                    height: 24px;
                    border: 3px solid rgba(255,255,255,0.1);
                    border-top-color: #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                
                .spinning {
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}
